import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { getApiBaseUrl, publicMediaSrc } from "../config/api";
import { productHref } from "../components/CategoryCard";
import DeliverySeo from "../components/DeliverySeo";
import { deliveryHref } from "../components/deliveryHref";
import useReveal from "../hooks/useReveal";
import { trackPublicPageview } from "../utils/trackPageview";
import NotFoundPage from "./NotFoundPage";
import deliveriesHero from "../assets/deliveries-hero.jpg";
import viewAllDeliveriesImg from "../assets/view-all-deliveries.jpg";

const PAGE_GUTTER = "px-16 md:px-32 lg:px-40";
const SWIPE_THRESHOLD = 40;

function lockDoubleClick(event) {
  const el = event.currentTarget;
  if (el.dataset.navLock === "1") {
    event.preventDefault();
    return;
  }
  el.dataset.navLock = "1";
  window.setTimeout(() => {
    delete el.dataset.navLock;
  }, 700);
}

function cleanText(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "undefined" || text === "null") return "";
  return text;
}

function idOf(value) {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value).trim();
}

function formatDeliveryDate(value, month = "long") {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month,
    year: "numeric",
  });
}

function isoDeliveryDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString();
}

function DeliveryDetailContent({ delivery, products, deliveries }) {
  const pageRef = useRef(null);
  const imageBtnRef = useRef(null);
  const lightboxRef = useRef(null);
  const closeBtnRef = useRef(null);
  const swipeRef = useRef({ x: 0, swiped: false });
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [brokenSrc, setBrokenSrc] = useState(() => new Set());

  useReveal(pageRef, idOf(delivery));

  const title = cleanText(delivery?.title);
  const location = cleanText(delivery?.deliveryLocation);
  const dateLabel = formatDeliveryDate(delivery?.deliveryDate);
  const dateTime = isoDeliveryDate(delivery?.deliveryDate);
  const description = cleanText(delivery?.description);

  const productById = useMemo(() => {
    const map = new Map();
    products.forEach((item) => {
      const id = idOf(item);
      if (id) map.set(id, item);
    });
    return map;
  }, [products]);

  const linkedProducts = useMemo(() => {
    const rows = Array.isArray(delivery?.product) ? delivery.product : [];
    return rows
      .map((row) => {
        const catalog = productById.get(idOf(row?.product));
        const name = cleanText(row?.productName) || cleanText(catalog?.productName);
        if (!name) return null;
        const href = catalog ? productHref(catalog) : "";
        return {
          key: `${idOf(row?.product) || name}`,
          name,
          categoryName: cleanText(catalog?.category?.categoryName),
          photo: publicMediaSrc(catalog?.image),
          photoAlt: cleanText(catalog?.imageAlt) || name,
          href: href && href !== "/products" ? href : "",
        };
      })
      .filter(Boolean);
  }, [delivery, productById]);

  const moreDeliveries = useMemo(() => {
    const currentId = idOf(delivery);
    return deliveries
      .filter((item) => idOf(item) !== currentId)
      .filter((item) => Array.isArray(item.images) && item.images[0]?.url)
      .sort((a, b) => new Date(b.deliveryDate || 0) - new Date(a.deliveryDate || 0))
      .slice(0, 3);
  }, [deliveries, delivery]);

  const images = useMemo(
    () => (Array.isArray(delivery?.images) ? delivery.images.filter((item) => publicMediaSrc(item?.url)) : []),
    [delivery]
  );

  const imageCount = images.length;
  const safeIndex = imageCount ? Math.min(activeImage, imageCount - 1) : 0;
  const currentImage = images[safeIndex] || images[0];
  const currentPhoto = publicMediaSrc(currentImage?.url);
  const currentAlt = cleanText(currentImage?.altText) || title;
  const suppliedCount = Array.isArray(delivery?.product) ? delivery.product.length : linkedProducts.length;
  const productNames = linkedProducts.map((item) => item.name).filter(Boolean);

  const stepImage = useCallback(
    (direction) => {
      if (imageCount < 2) return;
      setActiveImage((index) => (index + direction + imageCount) % imageCount);
    },
    [imageCount]
  );

  const markBroken = useCallback((src) => {
    if (!src) return;
    setBrokenSrc((prev) => {
      if (prev.has(src)) return prev;
      const next = new Set(prev);
      next.add(src);
      return next;
    });
  }, []);

  useEffect(() => {
    setActiveImage(0);
    setLightboxOpen(false);
  }, [delivery?._id]);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    const imageButton = imageBtnRef.current;
    return () => {
      document.body.style.overflow = previousOverflow;
      imageButton?.focus();
    };
  }, [lightboxOpen]);

  useEffect(() => {
    function onKey(event) {
      const tag = String(event.target?.tagName || "");
      if (tag === "INPUT" || tag === "TEXTAREA" || event.target?.isContentEditable) return;

      if (lightboxOpen && event.key === "Escape") {
        event.preventDefault();
        setLightboxOpen(false);
        return;
      }

      if (lightboxOpen && event.key === "Tab") {
        const root = lightboxRef.current;
        if (!root) return;
        const focusable = [...root.querySelectorAll("button")];
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }

      if (imageCount < 2) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        stepImage(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        stepImage(1);
      }
    }

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxOpen, imageCount, stepImage]);

  function onGalleryTouchStart(event) {
    const touch = event.changedTouches?.[0];
    swipeRef.current = { x: touch ? touch.clientX : 0, swiped: false };
  }

  function onGalleryTouchEnd(event) {
    if (imageCount < 2) return;
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    const delta = touch.clientX - swipeRef.current.x;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
    swipeRef.current.swiped = true;
    stepImage(delta < 0 ? 1 : -1);
  }

  function openLightbox() {
    if (swipeRef.current.swiped) {
      swipeRef.current.swiped = false;
      return;
    }
    if (currentPhoto && !brokenSrc.has(currentPhoto)) setLightboxOpen(true);
  }

  return (
    <div ref={pageRef}>
      <section className={`bg-white py-14 ${PAGE_GUTTER}`}>
        <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,1.45fr)_minmax(260px,0.75fr)] lg:gap-16">
          <div>
            <div className="delivery-gallery-frame aspect-[16/10] overflow-hidden bg-neutral-100">
              {currentPhoto ? (
                <button
                  type="button"
                  ref={imageBtnRef}
                  className="delivery-gallery-open"
                  onClick={openLightbox}
                  onTouchStart={onGalleryTouchStart}
                  onTouchEnd={onGalleryTouchEnd}
                  aria-label={`View larger image of ${currentAlt || title || "this delivery"}`}
                >
                  {images.map((image, index) => {
                    const src = publicMediaSrc(image.url);
                    if (!src || brokenSrc.has(src)) return null;
                    return (
                      <img
                        key={`${src}-${index}`}
                        src={src}
                        alt={index === safeIndex ? cleanText(image.altText) || title : ""}
                        width={1200}
                        height={800}
                        fetchPriority={index === 0 ? "high" : undefined}
                        loading={index === 0 ? "eager" : "lazy"}
                        decoding="async"
                        className={index === safeIndex ? "is-active" : ""}
                        onError={() => markBroken(src)}
                      />
                    );
                  })}
                </button>
              ) : null}
            </div>
            {imageCount > 1 ? (
              <ul className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
                {images.map((image, index) => {
                  const thumb = publicMediaSrc(image.url);
                  const selected = index === safeIndex;
                  return (
                    <li key={`${image.url}-${index}`}>
                      <button
                        type="button"
                        onClick={() => setActiveImage(index)}
                        aria-pressed={selected}
                        aria-label={`Show delivery photo ${index + 1}`}
                        className={`delivery-gallery-thumb block min-h-[44px] w-full overflow-hidden bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold ${selected ? "ring-2 ring-at-gold" : ""}`}
                      >
                        {thumb && !brokenSrc.has(thumb) ? (
                          <img
                            src={thumb}
                            alt=""
                            width={240}
                            height={160}
                            loading="lazy"
                            decoding="async"
                            className="aspect-[16/10] h-auto w-full object-cover"
                            onError={() => markBroken(thumb)}
                          />
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>

          <aside className="bg-neutral-100 p-6 md:p-8" data-reveal="">
            <h2 className="font-serif text-2xl uppercase text-at-dark">Delivery Details</h2>
            <ul className="mt-6 divide-y divide-neutral-200 text-sm">
              {location ? (
                <li className="flex items-start gap-3 py-4 first:pt-0">
                  <svg viewBox="0 0 24 24" className="mt-0.5 h-5 w-5 shrink-0 text-at-gold" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                    <path d="M12 21s7-5.3 7-11a7 7 0 10-14 0c0 5.7 7 11 7 11z" />
                    <circle cx="12" cy="10" r="2" />
                  </svg>
                  <span>
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-400">Location</span>
                    <span className="mt-1 block text-at-dark">{location}</span>
                  </span>
                </li>
              ) : null}
              {dateLabel ? (
                <li className="flex items-start gap-3 py-4">
                  <svg viewBox="0 0 24 24" className="mt-0.5 h-5 w-5 shrink-0 text-at-gold" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                    <rect x="4" y="5" width="16" height="15" rx="1.5" />
                    <path d="M8 3v4M16 3v4M4 10h16" />
                  </svg>
                  <span>
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-400">Delivery Date</span>
                    <span className="mt-1 block text-at-dark">
                      {dateTime ? <time dateTime={dateTime}>{dateLabel}</time> : dateLabel}
                    </span>
                  </span>
                </li>
              ) : null}
              {suppliedCount ? (
                <li className="flex items-start gap-3 py-4 last:pb-0">
                  <svg viewBox="0 0 24 24" className="mt-0.5 h-5 w-5 shrink-0 text-at-gold" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                    <path d="M3 8l9-4 9 4-9 4-9-4z" />
                    <path d="M3 8v8l9 4 9-4V8" />
                  </svg>
                  <span>
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-400">Products Supplied</span>
                    <span className="mt-1 block text-at-dark">
                      {suppliedCount} {suppliedCount === 1 ? "product" : "products"}
                    </span>
                  </span>
                </li>
              ) : null}
            </ul>
            <Link
              to="/products"
              state={{
                deliveryTitle: title,
                productNames,
                deliveryLocation: location,
                deliveryHref: delivery ? deliveryHref(delivery) : "",
              }}
              onClick={lockDoubleClick}
              className="hero-btn mt-8 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-at-gold px-5 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
            >
              Explore Products <span className="btn-arrow">→</span>
            </Link>
          </aside>
        </div>
      </section>

      {description ? (
        <section className={`bg-white pb-14 ${PAGE_GUTTER}`} data-reveal="">
          <h2 className="font-serif text-2xl uppercase text-at-dark md:text-3xl">Delivery Overview</h2>
          <p className="mt-4 max-w-4xl text-sm leading-relaxed text-neutral-600">{description}</p>
        </section>
      ) : null}

      {linkedProducts.length ? (
        <section className={`bg-neutral-100 py-14 ${PAGE_GUTTER}`} data-reveal="">
          <h2 className="font-serif text-2xl uppercase text-at-dark md:text-3xl">Products in This Delivery</h2>
          <ul className="mt-8 grid gap-4 md:grid-cols-2">
            {linkedProducts.map((item) => {
              const body = (
                <>
                  <span className="h-24 w-36 shrink-0 overflow-hidden bg-neutral-100">
                    {item.photo && !brokenSrc.has(item.photo) ? (
                      <img
                        src={item.photo}
                        alt={item.photoAlt}
                        width={288}
                        height={192}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover"
                        onError={() => markBroken(item.photo)}
                      />
                    ) : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-at-dark">{item.name}</span>
                    {item.categoryName ? (
                      <span className="mt-1 block text-xs text-neutral-500">{item.categoryName}</span>
                    ) : null}
                  </span>
                  {item.href ? (
                    <span className="card-arrow shrink-0 text-lg text-neutral-400" aria-hidden="true">
                      →
                    </span>
                  ) : null}
                </>
              );
              return (
                <li key={item.key}>
                  {item.href ? (
                    <Link
                      to={item.href}
                      className="delivery-product-row flex items-center gap-4 bg-white p-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-at-gold"
                    >
                      {body}
                    </Link>
                  ) : (
                    <div className="delivery-product-row flex items-center gap-4 bg-white p-3">{body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {moreDeliveries.length ? (
        <section className={`bg-neutral-50 py-14 ${PAGE_GUTTER}`} data-reveal="">
          <div className="flex items-end justify-between gap-4">
            <h2 className="font-serif text-3xl text-at-dark">More Recent Deliveries</h2>
            <Link to="/deliveries" className="hidden text-sm text-neutral-500 hover:text-at-dark sm:inline">
              View All Deliveries →
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {moreDeliveries.map((item) => {
              const firstImage = item.images[0];
              const photo = publicMediaSrc(firstImage.url);
              return (
                <Link
                  key={item._id || item.slug}
                  to={deliveryHref(item)}
                  className="media-card group relative flex aspect-square flex-col justify-end overflow-hidden bg-at-dark p-4 text-white"
                >
                  {photo && !brokenSrc.has(photo) ? (
                    <img
                      src={photo}
                      alt={firstImage.altText || item.title || ""}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 h-full w-full object-cover"
                      onError={() => markBroken(photo)}
                    />
                  ) : null}
                  <span className="absolute inset-0 bg-black/50" />
                  <span className="relative z-[2] flex items-end justify-between gap-2">
                    <span className="min-w-0">
                      <h3 className="block text-sm font-semibold leading-tight sm:text-base">{item.title}</h3>
                      <span className="mt-0.5 min-h-[2.75em] line-clamp-2 text-[10px] leading-snug text-at-gold sm:text-xs">
                        {formatDeliveryDate(item.deliveryDate, "short") || "\u00a0"}
                      </span>
                    </span>
                    <span className="card-arrow shrink-0 text-lg text-at-gold">→</span>
                  </span>
                </Link>
              );
            })}
            <Link
              to="/deliveries"
              className="media-card group relative flex aspect-square flex-col justify-end overflow-hidden bg-at-dark"
              aria-label="View all deliveries"
            >
              <img src={viewAllDeliveriesImg} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
            </Link>
          </div>
        </section>
      ) : null}

      {lightboxOpen && currentPhoto && !brokenSrc.has(currentPhoto) ? (
        <div
          ref={lightboxRef}
          className="product-lightbox delivery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={currentAlt || title}
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            ref={closeBtnRef}
            className="product-lightbox-close"
            aria-label="Close image"
            onClick={(event) => {
              event.stopPropagation();
              setLightboxOpen(false);
            }}
          >
            ×
          </button>
          {imageCount > 1 ? (
            <>
              <button
                type="button"
                className="delivery-lightbox-nav delivery-lightbox-prev"
                aria-label="Previous image"
                onClick={(event) => {
                  event.stopPropagation();
                  stepImage(-1);
                }}
              >
                ‹
              </button>
              <button
                type="button"
                className="delivery-lightbox-nav delivery-lightbox-next"
                aria-label="Next image"
                onClick={(event) => {
                  event.stopPropagation();
                  stepImage(1);
                }}
              >
                ›
              </button>
            </>
          ) : null}
          <img src={currentPhoto} alt={currentAlt} onClick={(event) => event.stopPropagation()} />
        </div>
      ) : null}
    </div>
  );
}

function DeliveryDetailPage() {
  const { dateStamp, slug } = useParams();
  const routeLocation = useLocation();
  const pageRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [deliveries, setDeliveries] = useState([]);
  const [products, setProducts] = useState([]);
  const [contact, setContact] = useState(null);
  const [contactStatus, setContactStatus] = useState("loading");
  const [loadNonce, setLoadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setDeliveries([]);
    Promise.all([
      fetch(`${getApiBaseUrl()}/deliveries`, { headers: { Accept: "application/json" } }).then((res) => {
        if (!res.ok) throw new Error("Unable to load deliveries");
        return res.json();
      }),
      fetch(`${getApiBaseUrl()}/products`, { headers: { Accept: "application/json" } })
        .then((res) => (res.ok ? res.json() : []))
        .catch(() => []),
    ])
      .then(([deliveryList, productList]) => {
        if (cancelled) return;
        setDeliveries((Array.isArray(deliveryList) ? deliveryList : []).filter((item) => item?.isPublished !== false));
        setProducts((Array.isArray(productList) ? productList : []).filter((item) => item?.isPublished !== false));
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setDeliveries([]);
        setProducts([]);
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [slug, dateStamp, loadNonce]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (cancelled) return;
        setContact(Array.isArray(list) && list[0] ? list[0] : null);
        setContactStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setContact(null);
        setContactStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const delivery = useMemo(
    () => deliveries.find((item) => cleanText(item?.slug) === cleanText(slug)) || null,
    [deliveries, slug]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [delivery?._id, slug, dateStamp]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nodes = pageRef.current?.querySelectorAll(".hero-in");
      nodes?.forEach((node) => {
        if (getComputedStyle(node).opacity === "0") node.classList.add("is-visible");
      });
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [delivery?._id]);

  const retry = useCallback(() => {
    setLoadNonce((value) => value + 1);
  }, []);

  const expectedPath = delivery ? deliveryHref(delivery) : "";
  const needsRedirect =
    status === "ready" && delivery && expectedPath && routeLocation.pathname !== expectedPath;

  useEffect(() => {
    if (status !== "ready" || !delivery || needsRedirect) return;
    trackPublicPageview({
      path: deliveryHref(delivery),
      title: cleanText(delivery.title),
    });
  }, [status, delivery, needsRedirect]);

  const seoStatus =
    status === "error"
      ? "error"
      : status === "loading"
        ? "loading"
        : !delivery
          ? "missing"
          : needsRedirect
            ? "redirect"
            : "ready";
  const seo = (
    <DeliverySeo
      contact={contact}
      contactStatus={contactStatus}
      delivery={seoStatus === "ready" || seoStatus === "redirect" ? delivery : null}
      products={products}
      status={seoStatus}
    />
  );

  if (needsRedirect) {
    return (
      <>
        {seo}
        <Navigate to={expectedPath} replace />
      </>
    );
  }

  if (status === "ready" && !delivery) {
    return <NotFoundPage />;
  }

  const title = cleanText(delivery?.title);
  const location = cleanText(delivery?.deliveryLocation);
  const dateLabel = formatDeliveryDate(delivery?.deliveryDate);
  const dateTime = isoDeliveryDate(delivery?.deliveryDate);

  return (
    <div className="delivery-detail-page" ref={pageRef}>
      {seo}
      <section className="relative overflow-hidden bg-at-dark text-white">
        <img
          src={deliveriesHero}
          alt=""
          width={1024}
          height={341}
          fetchPriority="high"
          className="hero-zoom-subtle pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="pointer-events-none absolute inset-0 bg-black/20" />
        <div className="pointer-events-none absolute inset-y-0 left-0 hidden h-full w-[58%] bg-gradient-to-r from-black/75 via-black/40 to-transparent md:block" />
        <div className={`relative z-10 flex min-h-[320px] flex-col justify-between py-10 md:min-h-[420px] md:py-12 ${PAGE_GUTTER}`}>
          <nav aria-label="Breadcrumb" className="hero-in text-xs text-white/70">
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <span className="px-2 text-white/40">/</span>
            <Link to="/deliveries" className="hover:text-white">
              Deliveries
            </Link>
            {title ? (
              <>
                <span className="px-2 text-white/40">/</span>
                <span className="text-white">{title}</span>
              </>
            ) : null}
          </nav>
          <div className="mb-6 max-w-2xl md:mb-10">
            <p className="hero-in hero-in-1 mt-8 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold">
              <span className="inline-block h-1 w-12 bg-at-gold" aria-hidden="true" />
              Delivery Update
            </p>
            <h1 className="hero-in hero-in-2 mt-2 font-serif text-3xl uppercase leading-tight md:text-5xl">
              {status === "loading" ? "Loading delivery" : title || "Delivery"}
            </h1>
            {status === "ready" ? (
              <p className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/70">
                {location ? (
                  <span className="hero-in hero-in-3 inline-flex items-center gap-1.5">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 text-at-gold" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                      <path d="M12 21s7-5.3 7-11a7 7 0 10-14 0c0 5.7 7 11 7 11z" />
                      <circle cx="12" cy="10" r="2" />
                    </svg>
                    {location}
                  </span>
                ) : null}
                {dateLabel ? (
                  <span className="hero-in hero-in-4 inline-flex items-center gap-1.5">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 text-at-gold" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                      <rect x="4" y="5" width="16" height="15" rx="1.5" />
                      <path d="M8 3v4M16 3v4M4 10h16" />
                    </svg>
                    {dateTime ? <time dateTime={dateTime}>{dateLabel}</time> : dateLabel}
                  </span>
                ) : null}
              </p>
            ) : null}
          </div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/45">
            Quality Materials Stronger Tomorrow
          </p>
        </div>
      </section>

      {status === "error" ? (
        <section className={`bg-white py-14 ${PAGE_GUTTER}`}>
          <p className="text-sm text-neutral-500">Unable to load this delivery.</p>
          <button
            type="button"
            onClick={(event) => {
              lockDoubleClick(event);
              retry();
            }}
            className="hero-btn mt-3 font-semibold text-at-dark underline decoration-at-gold underline-offset-4"
          >
            Retry
          </button>
        </section>
      ) : null}

      {status === "ready" && delivery ? (
        <DeliveryDetailContent delivery={delivery} products={products} deliveries={deliveries} />
      ) : null}
    </div>
  );
}

export default DeliveryDetailPage;
